import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  App as AntdApp,
  Card,
  Collapse,
  Descriptions,
  Empty,
  Table,
  Tag,
  Divider,
  Typography,
  Space,
  Button,
  Spin,
  Alert,
  Tooltip,
} from 'antd';
import {
  ArrowLeftOutlined,
  LinkOutlined,
  CreditCardOutlined,
} from '@ant-design/icons';
import { Invoice } from '../services/paymentService';
import { invoiceService } from '../services/invoiceService';
import FeesEditor from '../components/Shared/FeesEditor';
import { TaxExemptionToggle } from '../components/Shared/TaxExemptionToggle';
import { InternalNotesField } from '../components/Shared/InternalNotesField';
import { AuditTrailTimeline } from '../components/Shared/AuditTrailTimeline';
import { InspectionNotesSummary } from '../components/DVI/InspectionNotesSummary';
import { dviApi } from '../services/api/dviApi';
import { DVIInspectionItem } from '../types/dvi';
import { useIsMobile } from '../hooks/useIsMobile';

const { Title, Text } = Typography;

export interface InvoicePageProps {
  /** When true: no back-button, expects invoiceId prop instead of useParams */
  embedded?: boolean;
  /** Required when embedded=true */
  invoiceId?: number;
}

const InvoicePageInner: React.FC<InvoicePageProps> = ({ embedded, invoiceId }) => {
  const { id: paramId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = AntdApp.useApp();
  const isMobile = useIsMobile(768);

  const resolvedId = embedded ? invoiceId : Number(paramId);

  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [internalNotes, setInternalNotes] = useState('');
  const [taxRate, setTaxRate] = useState<number>(0);
  const [taxExempt, setTaxExempt] = useState<boolean>(false);
  const [shareLinkLoading, setShareLinkLoading] = useState(false);

  /**
   * WP-07 — the inspection's notes and condition values belong on the invoice:
   * it is the document the customer keeps, and "why did I pay for this" is
   * answered by the observation ("Wear: 2mm"), not by the line item alone.
   *
   * Fetched from the invoice's work order. Non-fatal by design — an invoice
   * must render and be payable whether or not an inspection exists.
   */
  const [inspectionItems, setInspectionItems] = useState<DVIInspectionItem[]>([]);
  useEffect(() => {
    const workOrderId = invoice?.work_order_id;
    if (!workOrderId) {
      setInspectionItems([]);
      return;
    }
    let cancelled = false;
    dviApi
      .get(workOrderId)
      .then((dvi) => { if (!cancelled) setInspectionItems(dvi?.items ?? []); })
      .catch(() => { if (!cancelled) setInspectionItems([]); });
    return () => { cancelled = true; };
  }, [invoice?.work_order_id]);

  // Persist internal notes on blur via PUT /admin/invoices/:id.
  const handleSaveInternalNotes = useCallback(
    async (notes: string) => {
      if (!invoice?.id) return;
      try {
        const updated = await invoiceService.updateInvoice(invoice.id, { internal_notes: notes });
        setInvoice(updated);
      } catch (err: any) {
        message.error(`Failed to save internal notes: ${err?.message ?? 'unknown error'}`);
        throw err; // let the field flip to its error state
      }
    },
    [invoice, message],
  );

  // Persist invoice-level tax-exemption override.
  const handleToggleTaxExempt = useCallback(
    async (next: boolean) => {
      if (!invoice?.id) return;
      try {
        const updated = await invoiceService.updateInvoice(invoice.id, { is_tax_exempt: next });
        setInvoice(updated);
        setTaxExempt(Boolean(updated.is_tax_exempt));
        message.success(next ? 'Invoice marked tax-exempt' : 'Tax exemption removed');
      } catch (err: any) {
        message.error(`Failed to update tax exemption: ${err?.message ?? 'unknown error'}`);
        throw err;
      }
    },
    [invoice, message],
  );

  const handleShareLink = useCallback(async () => {
    if (!invoice?.id) return;
    setShareLinkLoading(true);
    try {
      const { token } = await invoiceService.createShareLink(invoice.id, 'email');
      const portalUrl = `${window.location.origin}/portal/invoice/${token}`;
      await navigator.clipboard.writeText(portalUrl);
      message.success('Share link copied to clipboard');
    } catch (err: any) {
      console.error('Failed to create share link', err);
      message.error('Failed to create share link');
    } finally {
      setShareLinkLoading(false);
    }
  }, [invoice, message]);

  useEffect(() => {
    if (!resolvedId) return;
    setLoading(true);
    setError(null);
    invoiceService
      .getInvoiceById(resolvedId)
      .then((inv) => {
        setInvoice(inv);
        setInternalNotes(inv.internal_notes ?? '');
        setTaxRate(inv.tax_rate ?? 0);
        setTaxExempt(Boolean(inv.is_tax_exempt));
      })
      .catch((err: any) => setError(err?.message ?? 'Failed to load invoice'))
      .finally(() => setLoading(false));
  }, [resolvedId]);

  const lineItemColumns = [
    {
      title: 'Line #',
      dataIndex: 'line_number',
      key: 'line_number',
      width: 70,
    },
    {
      title: 'Description',
      key: 'description',
      render: (_: any, record: any) => (
        <div>
          <div style={{ fontWeight: 500, marginBottom: 4 }}>
            Concern: {record.concern}
          </div>
          {record.cause && (
            <div style={{ fontSize: 12, color: '#666' }}>
              Cause: {record.cause}
            </div>
          )}
          {record.correction && (
            <div style={{ fontSize: 12, color: '#666' }}>
              Correction: {record.correction}
            </div>
          )}
          {Boolean(record.is_diagnosis) && (
            <Tag
              color={record.is_diagnosis_waived ? 'green' : 'blue'}
              style={{ marginTop: 4 }}
            >
              {record.is_diagnosis_waived ? 'Diagnosis (WAIVED)' : 'Diagnosis'}
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Labor',
      key: 'labor',
      width: 120,
      render: (_: any, record: any) => (
        <div style={{ textAlign: 'right' as const }}>
          {record.labor_hours > 0 ? (
            <>
              <div>
                {record.labor_hours}h @ ${record.labor_rate}/h
              </div>
              {record.labor_discount_amount > 0 && (
                <div style={{ fontSize: 11, color: '#22c55e' }}>
                  -${record.labor_discount_amount.toFixed(2)} discount
                </div>
              )}
              <div style={{ fontWeight: 500 }}>
                ${record.labor_total?.toFixed(2) || '0.00'}
              </div>
            </>
          ) : (
            <div style={{ color: '#999' }}>—</div>
          )}
        </div>
      ),
    },
    {
      title: 'Parts',
      key: 'parts',
      width: 100,
      render: (_: any, record: any) => (
        <div style={{ textAlign: 'right' as const }}>
          {record.parts && record.parts.length > 0 ? (
            <div style={{ fontWeight: 500 }}>
              ${record.parts_total?.toFixed(2) || '0.00'}
            </div>
          ) : (
            <div style={{ color: '#999' }}>—</div>
          )}
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'line_total',
      key: 'line_total',
      width: 100,
      render: (total: number, record: any) => (
        <div style={{ textAlign: 'right' as const }}>
          {record.is_diagnosis_waived && record.original_line_total ? (
            <>
              <div
                style={{
                  textDecoration: 'line-through',
                  color: '#999',
                  fontSize: 11,
                }}
              >
                ${record.original_line_total.toFixed(2)}
              </div>
              <div style={{ fontWeight: 600, color: '#22c55e' }}>
                ${total.toFixed(2)} (FREE!)
              </div>
            </>
          ) : (
            <div style={{ fontWeight: 600 }}>${total.toFixed(2)}</div>
          )}
        </div>
      ),
    },
  ];

  // @ts-ignore TS2367: Invoice status type mismatch — pre-existing gap
  const isLocked = (invoice?.status as string) === 'sent' || invoice?.status === 'paid';

  if (loading) {
    return (
      <div style={{ padding: 32, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !invoice) {
    // Detect a 404 / not-found vs a genuine load failure. Never surface the raw
    // axios message ("Request failed with status code 404") to the user.
    const isNotFound = !error || /404|not found/i.test(error);
    return (
      <div style={{ padding: isMobile ? 16 : 32 }} data-testid="invoice-error">
        {!embedded && (
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            style={{ marginBottom: 16 }}
            data-testid="invoice-back-btn"
          >
            Back
          </Button>
        )}
        <Card style={{ maxWidth: 520, margin: '0 auto' }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              isNotFound
                ? "This invoice could not be found. It may have been deleted or the link is incorrect."
                : "We couldn't load this invoice. Please try again."
            }
          >
            {!embedded && (
              <Button type="primary" onClick={() => navigate(-1)}>
                Go Back
              </Button>
            )}
          </Empty>
        </Card>
      </div>
    );
  }

  return (
    <div
      style={{
        paddingTop: embedded ? 0 : (isMobile ? 12 : 24),
        paddingLeft: embedded ? 0 : (isMobile ? 12 : 24),
        paddingRight: embedded ? 0 : (isMobile ? 12 : 24),
        // On mobile add extra bottom padding so the sticky action bar doesn't overlap content
        paddingBottom: (!embedded && isMobile) ? 80 : (embedded ? 0 : (isMobile ? 12 : 24)),
      }}
      data-testid="invoice-page-root"
    >
      {/* Back button — only shown in page (non-embedded) mode */}
      {!embedded && (
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
            data-testid="invoice-back-btn"
          >
            Back
          </Button>
          {/* Action bar: Share Link + Pay */}
          <Space>
            <Button
              icon={<LinkOutlined />}
              loading={shareLinkLoading}
              onClick={handleShareLink}
              data-testid="share-link-btn"
            >
              Share Link with Customer
            </Button>
            {invoice.status !== 'paid' && (
              <Tooltip title="Opens payment flow for this invoice">
                <Button
                  type="primary"
                  icon={<CreditCardOutlined />}
                  href={`/payments?invoiceId=${invoice.id}`}
                  data-testid="pay-with-stripe-btn"
                >
                  Pay with Stripe
                </Button>
              </Tooltip>
            )}
          </Space>
        </div>
      )}

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Header */}
        <div>
          <Title level={4} style={{ margin: 0 }}>
            Invoice Details
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            {invoice.invoice_number}
          </Text>
        </div>

        {/* Status Badge */}
        <div style={{ textAlign: 'center' }}>
          {invoice.status === 'paid' ? (
            <Tag
              color="success"
              style={{ fontSize: 16, padding: '8px 16px', fontWeight: 600 }}
            >
              PAID IN FULL
            </Tag>
          ) : (
            <Tag
              color={invoice.status === 'void' ? 'default' : 'warning'}
              style={{ fontSize: 16, padding: '8px 16px', fontWeight: 600 }}
            >
              {invoice.status.toUpperCase()}
            </Tag>
          )}
        </div>

        {/* Invoice Info */}
        <Descriptions bordered size="small" column={2}>
          <Descriptions.Item label="Invoice Number" span={1}>
            <Text strong data-testid="invoice-number">
              {invoice.invoice_number}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Invoice Date" span={1}>
            {new Date(invoice.invoice_date).toLocaleDateString()}
          </Descriptions.Item>
          <Descriptions.Item label="Payment Date" span={1}>
            {invoice.payment_date
              ? new Date(invoice.payment_date).toLocaleDateString()
              : 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Payment Method" span={1}>
            {invoice.payment_method === 'card'
              ? 'Credit/Debit Card'
              : invoice.payment_method === 'cash'
              ? 'Cash'
              : invoice.payment_method === 'check'
              ? 'Check'
              : invoice.payment_method === 'affirm'
              ? 'Affirm'
              : invoice.payment_method || 'N/A'}
          </Descriptions.Item>
          <Descriptions.Item label="Status" span={2}>
            <Tag
              color={
                invoice.status === 'paid'
                  ? 'success'
                  : invoice.status === 'void'
                  ? 'default'
                  : 'warning'
              }
            >
              {invoice.status.toUpperCase()}
            </Tag>
            {Boolean(invoice.is_archived) && <Tag color="blue">ARCHIVED</Tag>}
          </Descriptions.Item>
        </Descriptions>

        <Divider />

        {/* Line Items */}
        <div>
          <Title level={5}>Services &amp; Parts</Title>
          {isLocked && (
            <Alert
              type="warning"
              message="Invoice has been sent — line items are locked"
              style={{ marginBottom: 8 }}
              data-testid="line-items-locked-banner"
            />
          )}
          {isMobile ? (
            /* Mobile card layout */
            <div data-testid="line-items-mobile">
              {(invoice.line_items || []).map((item: any) => (
                <div
                  key={item.id}
                  style={{
                    border: '1px solid #f0f0f0',
                    borderRadius: 8,
                    padding: 12,
                    marginBottom: 8,
                    backgroundColor: '#fff',
                  }}
                >
                  <div style={{ fontWeight: 500, marginBottom: 4 }}>
                    {item.line_number}. {item.concern}
                  </div>
                  {item.cause && (
                    <div style={{ fontSize: 12, color: '#666' }}>Cause: {item.cause}</div>
                  )}
                  {item.correction && (
                    <div style={{ fontSize: 12, color: '#666' }}>Correction: {item.correction}</div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginTop: 8,
                    }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Labor: ${(item.labor_total || 0).toFixed(2)} | Parts: ${(item.parts_total || 0).toFixed(2)}
                    </Text>
                    <Text strong>${(item.line_total ?? 0).toFixed(2)}</Text>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <Table
              dataSource={invoice.line_items || []}
              columns={lineItemColumns}
              pagination={false}
              rowKey="id"
              size="small"
              data-testid="line-items-table"
              expandable={{
                expandedRowRender: (record: any) => {
                  if (!record.parts || record.parts.length === 0) return null;
                  return (
                    <div style={{ padding: 16, backgroundColor: '#fafafa' }}>
                      <Text strong>Parts Used:</Text>
                      <Table
                        dataSource={record.parts}
                        size="small"
                        pagination={false}
                        rowKey="id"
                        columns={[
                          { title: 'Part Name', dataIndex: 'part_name', key: 'part_name' },
                          { title: 'Part Number', dataIndex: 'part_number', key: 'part_number' },
                          { title: 'Qty', dataIndex: 'quantity', key: 'quantity', width: 60 },
                          {
                            title: 'Unit Price',
                            dataIndex: 'unit_price',
                            key: 'unit_price',
                            width: 100,
                            render: (price: number) => `$${(price ?? 0).toFixed(2)}`,
                          },
                          {
                            title: 'Total',
                            dataIndex: 'total_price',
                            key: 'total_price',
                            width: 100,
                            render: (price: number) => `$${(price ?? 0).toFixed(2)}`,
                          },
                        ]}
                      />
                    </div>
                  );
                },
                rowExpandable: (record: any) => record.parts && record.parts.length > 0,
              }}
            />
          )}
        </div>

        {/* Fees — between line items and totals */}
        {invoice.id && (
          <div data-testid="fees-section">
            <Title level={5} style={{ marginBottom: 8 }}>Fees</Title>
            <FeesEditor
              entityType="invoices"
              entityId={invoice.id}
              editable={!isLocked}
            />
          </div>
        )}

        {/* Tax exemption notice */}
        {invoice.customer && (
          <div data-testid="tax-exempt-section">
            <TaxExemptionToggle
              customer={invoice.customer}
              onTaxRateChange={setTaxRate}
              editable
              invoiceTaxExempt={taxExempt}
              onToggleExempt={handleToggleTaxExempt}
            />
          </div>
        )}

        <Divider />

        {/* Totals */}
        <div
          style={{ backgroundColor: '#f5f5f5', padding: 16, borderRadius: 8 }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text>Subtotal:</Text>
              <Text strong>${(invoice.subtotal ?? 0).toFixed(2)}</Text>
            </div>
            {((invoice as any).discount_amount ?? 0) > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <Text>
                  Discount
                  {(invoice as any).discount_type === 'percentage' && (invoice as any).discount_value
                    ? ` (${(invoice as any).discount_value}%)`
                    : ''}
                  :
                </Text>
                <Text strong style={{ color: '#ef4444' }}>
                  -${((invoice as any).discount_amount ?? 0).toFixed(2)}
                </Text>
              </div>
            )}
            {(invoice.tax_amount ?? 0) > 0 && (
              <div
                style={{ display: 'flex', justifyContent: 'space-between' }}
              >
                <Text>Tax ({(invoice.tax_rate ?? 0).toFixed(2)}%):</Text>
                <Text strong>${(invoice.tax_amount ?? 0).toFixed(2)}</Text>
              </div>
            )}
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 16 }}>
                Total Amount:
              </Text>
              <Text strong style={{ fontSize: 20, color: '#22c55e' }}>
                ${(invoice.total_amount ?? 0).toFixed(2)}
              </Text>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Text strong style={{ fontSize: 16 }}>
                Amount Paid:
              </Text>
              <Text strong style={{ fontSize: 20, color: '#22c55e' }}>
                ${(invoice.amount_paid ?? 0).toFixed(2)}
              </Text>
            </div>
            {invoice.status === 'paid' && (
              <>
                <Divider style={{ margin: '8px 0' }} />
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: 12,
                    backgroundColor: '#f0fdf4',
                    borderRadius: 6,
                    border: '2px solid #22c55e',
                  }}
                >
                  <Text strong style={{ fontSize: 18, color: '#22c55e' }}>
                    PAID IN FULL - Balance: $0.00
                  </Text>
                </div>
              </>
            )}
          </Space>
        </div>

        {invoice.notes && (
          <div>
            <Text strong>Notes:</Text>
            <div
              style={{
                marginTop: 8,
                padding: 12,
                backgroundColor: '#f5f5f5',
                borderRadius: 6,
              }}
            >
              {invoice.notes}
            </div>
          </div>
        )}

        {/* Inspection findings — notes + condition values (WP-07) */}
        {inspectionItems.length > 0 && (
          <div data-testid="invoice-inspection-section">
            <Divider />
            <InspectionNotesSummary items={inspectionItems} title="Inspection Findings" />
          </div>
        )}

        {/* Internal Notes — always editable, not shown to customer */}
        <div data-testid="internal-notes-section">
          <InternalNotesField
            value={internalNotes}
            onChange={setInternalNotes}
            onSave={handleSaveInternalNotes}
          />
        </div>

        {/* Audit Trail — collapsible */}
        {invoice.id && (
          <Collapse
            ghost
            items={[
              {
                key: 'audit',
                label: 'Activity Log',
                children: (
                  <div data-testid="audit-trail-section">
                    <AuditTrailTimeline
                      entityType="invoices"
                      entityId={invoice.id}
                    />
                  </div>
                ),
              },
            ]}
          />
        )}
      </Space>

      {/* Sticky bottom action bar — mobile only, non-embedded */}
      {!embedded && isMobile && (
        <div
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            padding: '12px 16px',
            backgroundColor: '#fff',
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            gap: 8,
            zIndex: 100,
          }}
          data-testid="mobile-action-bar"
        >
          <Button
            icon={<LinkOutlined />}
            loading={shareLinkLoading}
            onClick={handleShareLink}
            style={{ flex: 1 }}
            data-testid="share-link-btn-mobile"
          >
            Share
          </Button>
          {invoice.status !== 'paid' && (
            <Button
              type="primary"
              icon={<CreditCardOutlined />}
              href={`/payments?invoiceId=${invoice.id}`}
              style={{ flex: 1 }}
              data-testid="pay-with-stripe-btn-mobile"
            >
              Pay
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Wraps InvoicePageInner in an AntdApp context so `message` / `notification`
 * work correctly via useApp(). The wrapper is transparent when the page is
 * used inside a parent that already provides AntdApp.
 */
const InvoicePage: React.FC<InvoicePageProps> = (props) => (
  <AntdApp>
    <InvoicePageInner {...props} />
  </AntdApp>
);

export default InvoicePage;
